import { type BacklogHorizon, TIME_HORIZON_ORDER } from "./backlogOrder.ts";

/**
 * **What a family calls each planning bucket** — the three headings above the list
 * (issue #310, `Backlog.dc.html`; here since issue #387).
 *
 * ## Why the words moved here
 *
 * They were `apps/my.iamboone.app/src/horizons.ts`'s, under this package's own rule stated
 * there: core holds *words more than one runtime must agree on*, and while one screen said
 * these there was nothing yet to share — *"the day a tool speaks them, they move, the way
 * `CONTEXT_KIND_HEADING` already did."* No tool speaks them still; a second **surface** does.
 * Boone's app puts the same three headings above the same list, and a family reading *Soon*
 * on the panel and *Later* on their phone has been shown two names for one bucket, which is
 * the drift `contextKinds.ts` exists to prevent. `TIME_HORIZON_ORDER` was already here for
 * the same reason one level down: the order is `get_backlog`'s own.
 *
 * ## The headings are the canvas's
 *
 * *This week* and *Soon* are read off `Backlog.dc.html`. **`later` → *Soon*, not *Later*** is
 * the one worth stating: *later* is the schema's word for the far bucket and it reads as a
 * shrug — a place things go to be forgotten — where the canvas's *Soon* says the family still
 * means to do it. That is the difference between a backlog and a list somebody keeps.
 *
 * ## A horizon this build has never heard of is shown, not swallowed
 *
 * `timeHorizon` arrives as a plain string on the wire. A fourth bucket added server-side
 * reaches an older bundle — or an older *app*, which is the client that cannot be redeployed
 * (ADR 0025 decision 11) — with no heading for it, and dropping those items would make a
 * screen that puts a count beside every heading quietly wrong. So {@link isHorizon} narrows,
 * and both surfaces group the rest under their raw value where they stay readable,
 * correctable and deletable.
 */
export const HORIZON_HEADING: Readonly<Record<BacklogHorizon, string>> = {
  today: "Today",
  this_week: "This week",
  later: "Soon",
};

export function isHorizon(value: string): value is BacklogHorizon {
  return (TIME_HORIZON_ORDER as readonly string[]).includes(value);
}

/**
 * The buckets as a picker offers them — the same three, in the same order, with their headings.
 *
 * Derived rather than written out a second time, so *When?* on an edit form and the heading
 * above the row it sits in can never disagree.
 */
export const HORIZON_CHOICE: readonly { readonly value: BacklogHorizon; readonly label: string }[] =
  TIME_HORIZON_ORDER.map((value) => ({ value, label: HORIZON_HEADING[value] }));
